import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

const SESSION_KEY = "ss_popup_seen";

export default function HomePopup() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("order");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    api.get("/settings").then((r) => {
      const cfg = r.data || {};
      if (!cfg.popup_enabled) return;
      if (cfg.popup_show_until) {
        const d = Date.parse(cfg.popup_show_until);
        if (Number.isFinite(d) && d < Date.now()) return;
      }
      setS(cfg);
      setOpen(true);
      sessionStorage.setItem(SESSION_KEY, "1");
    }).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/notify/subscribe", { email, phone });
      toast.success(data.message);
      setSubscribed(true);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  if (!open || !s) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="home-popup">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md mx-auto p-7 fade-in">
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-[#111827] flex items-center justify-center"
          aria-label="Close"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center pt-1">
          <p className="text-3xl mb-2">🍪</p>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-gradient leading-tight" data-testid="popup-title">
            {s.popup_title || "ORDER NOW!!"}
          </h2>
        </div>

        <div className="mt-5 flex p-1 bg-[#FFF8F2] rounded-full">
          <button
            onClick={() => setTab("order")}
            className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${tab === "order" ? "bg-white shadow text-[#FF1F87]" : "text-[#4B5563]"}`}
            data-testid="popup-tab-order"
          >
            Order Now
          </button>
          <button
            onClick={() => setTab("notify")}
            className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${tab === "notify" ? "bg-white shadow text-[#FF1F87]" : "text-[#4B5563]"}`}
            data-testid="popup-tab-notify"
          >
            Notify Me
          </button>
        </div>

        {tab === "order" ? (
          <>
            <div className="mt-5 space-y-3">
              {s.popup_order_deadline && (
                <div className="rounded-2xl bg-[#FFF8F2] p-4 text-center">
                  <p className="text-[11px] uppercase tracking-widest font-bold text-[#FF1F87]">Order Deadline</p>
                  <p className="font-display font-extrabold text-xl mt-1" data-testid="popup-deadline">{s.popup_order_deadline}</p>
                </div>
              )}
              {s.popup_pickup_date && (
                <div className="rounded-2xl bg-[#FFE4EE] p-4 text-center">
                  <p className="text-[11px] uppercase tracking-widest font-bold text-[#FF1F87]">Orders Ready</p>
                  <p className="font-display font-extrabold text-xl mt-1" data-testid="popup-pickup">{s.popup_pickup_date}</p>
                </div>
              )}
              {s.popup_message && (
                <p className="text-center text-[#4B5563] text-sm leading-relaxed" data-testid="popup-message">{s.popup_message}</p>
              )}
            </div>

            <div className="mt-6 space-y-3">
              <button onClick={() => { setOpen(false); nav("/order"); }} className="btn-pink w-full" data-testid="popup-order-btn">ORDER NOW</button>
              <button onClick={() => setOpen(false)} className="btn-outline-pink w-full" data-testid="popup-dismiss-btn">CLOSE</button>
            </div>
          </>
        ) : (
          <div className="mt-5" data-testid="popup-notify-panel">
            {subscribed ? (
              <div className="text-center py-4">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                  <Check className="w-7 h-7" />
                </div>
                <p className="font-display font-extrabold text-xl">You're on the list!</p>
                <p className="text-sm text-[#4B5563] mt-1">We'll email you the moment new orders open.</p>
                <button onClick={() => setOpen(false)} className="btn-pink mt-5">DONE</button>
              </div>
            ) : (
              <form onSubmit={subscribe} className="space-y-3">
                <div className="text-center mb-1">
                  <Bell className="w-7 h-7 mx-auto text-[#FF1F87]" />
                  <p className="text-sm text-[#4B5563] mt-2">Get notified the moment we open new orders or drop a new flavor.</p>
                </div>
                <input type="email" required className="input-base" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="popup-notify-email" />
                <input type="tel" className="input-base" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="popup-notify-phone" />
                <button type="submit" disabled={submitting} className="btn-pink w-full" data-testid="popup-notify-submit">
                  {submitting ? "JOINING..." : "NOTIFY ME"}
                </button>
                <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#4B5563] underline w-full">No thanks</button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
